// BiuroPaczka Catalog Browser for adding new products

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import biuropaczkaData from '@/data/biuropaczka_products.json';

interface BiuroPaczkaCatalogProps {
  open: boolean;
  onClose: () => void;
  onAddProduct: (product: any, stockLevels: { min: number; max: number; initial: number }) => void;
  existingProductIds: string[];
}

interface AddProductDialogState {
  open: boolean;
  product: any | null;
  minLevel: number;
  maxLevel: number;
  initialStock: number;
}

export default function BiuroPaczkaCatalog({ 
  open, 
  onClose, 
  onAddProduct, 
  existingProductIds 
}: BiuroPaczkaCatalogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [addDialog, setAddDialog] = useState<AddProductDialogState>({
    open: false,
    product: null,
    minLevel: 5,
    maxLevel: 30,
    initialStock: 10
  });

  // Get available products (not already in inventory)
  const availableProducts = useMemo(() => {
    return biuropaczkaData.products.filter(product => 
      !existingProductIds.includes(product.id.toString())
    );
  }, [existingProductIds]);

  // Get unique categories from available products
  const categories = Array.from(new Set(availableProducts.map(p => p.category)));

  // Filter products
  const filteredProducts = useMemo(() => {
    return availableProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !categoryFilter || product.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  }, [availableProducts, searchTerm, categoryFilter]);

  const handleAddToInventory = (product: any) => {
    setAddDialog({
      open: true,
      product,
      minLevel: 5,
      maxLevel: 30,
      initialStock: 10
    });
  };

  const handleConfirmAdd = () => {
    if (addDialog.product) {
      onAddProduct(addDialog.product, {
        min: addDialog.minLevel,
        max: addDialog.maxLevel,
        initial: addDialog.initialStock
      });
      
      setAddDialog({
        open: false,
        product: null,
        minLevel: 5,
        maxLevel: 30,
        initialStock: 10
      });
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialog({
      open: false,
      product: null,
      minLevel: 5,
      maxLevel: 30,
      initialStock: 10
    });
  };

  return (
    <>
      {/* Main Catalog Dialog */}
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                component="img"
                src="https://biuropaczka.pl/static/version4fb3c9ef841dc2a9521b23425fcfa8773aff0c75/frontend/Snowdog/BiuroPaczka/pl_PL/images/bp-logo.svg"
                alt="BiuroPaczka Logo"
                sx={{ height: 24 }}
              />
              <Typography variant="h6">
                Katalog produktów BiuroPaczka
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box mb={3}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Katalog zawiera {availableProducts.length} dostępnych produktów</strong> gotowych do dodania do Twojego magazynu.
                Produkty już znajdujące się w magazynie są ukryte.
              </Typography>
            </Alert>

            {/* Filters */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Szukaj produktów w katalogu BiuroPaczka..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Kategoria</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Kategoria"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="">Wszystkie kategorie</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Znaleziono: {filteredProducts.length} produktów
            </Typography>
          </Box>

          {/* Products Grid */}
          <Grid container spacing={2} sx={{ maxHeight: 'calc(90vh - 300px)', overflow: 'auto' }}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  {/* Product Image */}
                  <CardMedia
                    component="img"
                    sx={{
                      height: 140,
                      objectFit: 'contain',
                      bgcolor: 'grey.50',
                      p: 1
                    }}
                    image={product.image_url || undefined}
                    alt={product.name}
                  />

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Product Info */}
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      {product.name}
                    </Typography>
                    
                    <Chip 
                      label={product.category} 
                      size="small" 
                      variant="outlined" 
                      sx={{ mb: 2, alignSelf: 'flex-start' }}
                    />

                    {/* Price */}
                    <Typography variant="h6" color="primary" fontWeight="bold" mb={2}>
                      {Math.floor(Math.random() * 100) + 10} zł
                    </Typography>

                    {/* Add Button */}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddToInventory(product)}
                      fullWidth
                      sx={{ mt: 'auto' }}
                    >
                      Dodaj do magazynu
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <Box textAlign="center" py={8}>
              <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Brak produktów spełniających kryteria
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Spróbuj zmienić filtry wyszukiwania
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} color="inherit">
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Configuration Dialog */}
      <Dialog 
        open={addDialog.open} 
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Dodaj produkt do magazynu
          </Typography>
        </DialogTitle>

        <DialogContent>
          {addDialog.product && (
            <Box>
              {/* Product Preview */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" gap={2} alignItems="center">
                    <Box
                      component="img"
                      src={addDialog.product.image_url}
                      alt={addDialog.product.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'contain',
                        bgcolor: 'grey.50',
                        borderRadius: 1
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {addDialog.product.name}
                      </Typography>
                      <Chip 
                        label={addDialog.product.category} 
                        size="small" 
                        variant="outlined" 
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Divider sx={{ mb: 3 }} />

              {/* Stock Level Configuration */}
              <Typography variant="h6" gutterBottom>
                Konfiguracja poziomów magazynowych
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Stan początkowy"
                    type="number"
                    value={addDialog.initialStock}
                    onChange={(e) => setAddDialog(prev => ({
                      ...prev,
                      initialStock: parseInt(e.target.value) || 0
                    }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Poziom minimalny"
                    type="number"
                    value={addDialog.minLevel}
                    onChange={(e) => setAddDialog(prev => ({
                      ...prev,
                      minLevel: parseInt(e.target.value) || 0
                    }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Poziom maksymalny"
                    type="number"
                    value={addDialog.maxLevel}
                    onChange={(e) => setAddDialog(prev => ({
                      ...prev,
                      maxLevel: parseInt(e.target.value) || 0
                    }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Sugerowane wartości:</strong> Stan początkowy: 10-20 szt, 
                  Minimum: 5 szt, Maksimum: 30 szt
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddDialog} color="inherit">
            Anuluj
          </Button>
          <Button 
            onClick={handleConfirmAdd} 
            variant="contained"
            startIcon={<AddIcon />}
          >
            Dodaj do magazynu
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}